import { useState } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MessageSquare, MapPin, Clock, Send, Users } from 'lucide-react';
import Layout from '@/components/Layout';
import { CONTACT_INFO } from '@/lib/index';
import { springPresets } from '@/lib/motion';

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <Layout>
      {/* Hero */}
      <section className="relative h-64 overflow-hidden">
        <img src="/images/cabin-crew.jpg" alt="联系我们" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-secondary/60 to-secondary/85" />
        <div className="absolute inset-0 flex items-center pt-20">
          <div className="max-w-7xl mx-auto px-4">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-2">联系我们</h1>
            <p className="text-white/70">坦桑尼亚航空中国代表处（广州）— 为您提供中文专属服务</p>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
            {/* Contact Channels */}
            <div className="space-y-5">
              <h2 className="text-xl font-bold mb-6">联系渠道</h2>

              {[
                {
                  icon: Phone,
                  color: 'text-green-600 bg-green-50 border-green-200',
                  title: '客服电话',
                  subtitle: '广州代表处',
                  value: CONTACT_INFO.phone,
                  href: `tel:${CONTACT_INFO.phone}`,
                  note: '工作日 09:00–18:00（北京时间）',
                },
                {
                  icon: Mail,
                  color: 'text-primary bg-primary/5 border-primary/20',
                  title: '旅客服务邮箱',
                  subtitle: '航班预订 · 行程更改 · 投诉建议',
                  value: CONTACT_INFO.passengerEmail,
                  href: `mailto:${CONTACT_INFO.passengerEmail}`,
                  note: '工作日24小时内回复',
                },
                {
                  icon: Mail,
                  color: 'text-accent bg-accent/10 border-accent/20',
                  title: '代理人服务邮箱',
                  subtitle: '旅行社 · 企业差旅 · BSP代理',
                  value: CONTACT_INFO.agentEmail,
                  href: `mailto:${CONTACT_INFO.agentEmail}`,
                  note: '专业商务支持',
                },
                {
                  icon: MessageSquare,
                  color: 'text-blue-600 bg-blue-50 border-blue-200',
                  title: 'QQ 服务群',
                  subtitle: '在线快速答疑',
                  value: CONTACT_INFO.qqGroup,
                  href: `https://qm.qq.com/q/${CONTACT_INFO.qqGroup}`,
                  note: '群号：' + CONTACT_INFO.qqGroup,
                },
                {
                  icon: Users,
                  color: 'text-red-600 bg-red-50 border-red-200',
                  title: '官方微博',
                  subtitle: '@Air Tanzania坦桑尼亚航空',
                  value: '微博：@Air Tanzania坦桑尼亚航空',
                  href: CONTACT_INFO.weibo,
                  note: '实时动态 · 促销信息',
                },
              ].map((item) => (
                <motion.a
                  key={item.title}
                  href={item.href}
                  target={item.href.startsWith('http') ? '_blank' : undefined}
                  rel="noopener noreferrer"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={springPresets.gentle}
                  className="flex items-start gap-4 p-4 rounded-2xl border border-border bg-card hover:shadow-md transition-all group"
                >
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center border ${item.color} shrink-0`}>
                    <item.icon size={18} />
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold text-sm">{item.title}</div>
                    <div className="text-xs text-muted-foreground mb-1">{item.subtitle}</div>
                    <div className="text-sm text-foreground font-medium break-all group-hover:text-primary transition-colors">{item.value}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{item.note}</div>
                  </div>
                </motion.a>
              ))}

              {/* Address */}
              <div className="p-4 rounded-2xl border border-border bg-card">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-secondary/10 border border-secondary/20 flex items-center justify-center shrink-0">
                    <MapPin size={18} className="text-secondary" />
                  </div>
                  <div>
                    <div className="font-semibold text-sm">办公地址</div>
                    <div className="text-xs text-muted-foreground mt-1 space-y-1.5">
                      <div className="text-foreground font-medium">广州市天河区林和西路9号</div>
                      <div>耀中广场（ChinaShine Plaza）A座 1323室</div>
                      <a
                        href="https://maps.google.com/maps?q=耀中广场+ChinaShine+Plaza+广州+天河区+林和西路9号"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-primary hover:underline font-medium mt-1"
                      >
                        <MapPin size={11} /> 在 Google Maps 中查看
                      </a>
                      <span className="mx-1.5 text-muted-foreground/40">·</span>
                      <a
                        href="https://uri.amap.com/marker?position=113.334,23.141&name=耀中广场ChinaShine+Plaza&src=airtanzania&coordinate=gaode"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-primary hover:underline font-medium"
                      >
                        高德地图
                      </a>
                      <div className="mt-1 pt-1 border-t border-border text-muted-foreground">总部：P.O.BOX 543, Dar es Salaam, Tanzania</div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="p-4 rounded-2xl border border-border bg-muted text-sm">
                <div className="flex items-center gap-2 text-muted-foreground mb-2">
                  <Clock size={14} />
                  <span className="font-medium">工作时间</span>
                </div>
                <div className="text-muted-foreground space-y-1 text-xs">
                  <div>周一至周五：09:00 – 18:00（北京时间）</div>
                  <div>周六：09:00 – 13:00</div>
                  <div>法定节假日：休息</div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <h2 className="text-xl font-bold mb-6">在线留言</h2>
              {submitted ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-16 bg-primary/5 border border-primary/20 rounded-2xl"
                >
                  <div className="text-5xl mb-4">✅</div>
                  <h3 className="text-xl font-bold mb-2">留言提交成功！</h3>
                  <p className="text-muted-foreground">我们将在工作日24小时内通过邮件或电话与您联系。</p>
                  <p className="text-muted-foreground text-sm mt-1">如有紧急事务，请直接拨打 {CONTACT_INFO.phone}</p>
                  <button
                    className="mt-6 text-primary text-sm underline"
                    onClick={() => { setSubmitted(false); setForm({ name: '', email: '', phone: '', subject: '', message: '' }); }}
                  >
                    重新提交
                  </button>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium mb-1.5">姓名 <span className="text-destructive">*</span></label>
                      <input
                        type="text"
                        required
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors text-sm"
                        placeholder="您的姓名"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1.5">电话</label>
                      <input
                        type="tel"
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors text-sm"
                        placeholder="联系电话（可选）"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1.5">邮箱 <span className="text-destructive">*</span></label>
                    <input
                      type="email"
                      required
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors text-sm"
                      placeholder="您的邮箱地址"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1.5">咨询主题 <span className="text-destructive">*</span></label>
                    <select
                      required
                      value={form.subject}
                      onChange={(e) => setForm({ ...form, subject: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors text-sm text-foreground"
                    >
                      <option value="">请选择咨询主题</option>
                      <option>航班预订与改签</option>
                      <option>行李规定查询</option>
                      <option>签证与出入境</option>
                      <option>Twiga Miles 积分</option>
                      <option>货运服务咨询</option>
                      <option>代理合作</option>
                      <option>投诉与建议</option>
                      <option>其他</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1.5">留言内容 <span className="text-destructive">*</span></label>
                    <textarea
                      required
                      rows={6}
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors text-sm resize-none"
                      placeholder="请详细描述您的问题或需求，包括航班号、日期、订单号等信息（如有）..."
                    />
                  </div>
                  <div className="text-xs text-muted-foreground">
                    提交即表示您同意我们按照相关法律法规处理您的个人信息，仅用于回复您的咨询。
                  </div>
                  <button
                    type="submit"
                    className="w-full flex items-center justify-center gap-2 py-4 bg-secondary hover:bg-secondary/90 text-white rounded-xl font-semibold transition-colors"
                  >
                    <Send size={16} />
                    提交留言
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
